/**
 * The staff the learner writes on.
 *
 * The lines are drawn by VexFlow rather than by hand, which matters more than it
 * sounds: we then read the stave's real geometry back out (`getYForLine`,
 * `getSpacingBetweenLines`, `getNoteStartX`) and hand it to the ink layer and the
 * recogniser. Handwriting is therefore measured against the exact same coordinates
 * that the engraved notation uses, so nothing drifts between the two layers.
 */

import { useEffect, useRef } from 'react';
import { Renderer, Stave } from 'vexflow';
import type { Clef, StaffLayout } from '../theory/staff';

/** VexFlow's built-in distance between staff lines, in its own coordinate space. */
export const VEX_LINE_GAP = 10;

export interface StaffPaperProps {
  clef: Clef;
  keySignature?: string;
  width: number;
  height: number;
  /** Give the dedicated Studio a larger, Pencil-first engraving scale. */
  studio?: boolean;
  /** Draw faint guides on the ledger-line positions above and below the staff. */
  showLedgerGuides?: boolean;
  onLayout: (layout: StaffLayout) => void;
}

export function StaffPaper({
  clef,
  keySignature,
  width,
  height,
  studio = false,
  showLedgerGuides = true,
  onLayout,
}: StaffPaperProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLCanvasElement>(null);
  const onLayoutRef = useRef(onLayout);
  onLayoutRef.current = onLayout;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || width <= 0 || height <= 0) return;

    host.innerHTML = '';
    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();

    // VexFlow's `spacing_between_lines_px` moves the staff lines but leaves clefs,
    // noteheads, and note placement at their default 10px-per-line scale, which
    // pulls the whole drawing apart. Scaling the context instead keeps every glyph
    // in proportion, so we lay out in VexFlow's native units and scale up.
    const targetLineGap = Math.max(16, Math.min(studio ? 46 : 30, Math.round(height / 11)));
    const scale = targetLineGap / VEX_LINE_GAP;
    ctx.scale(scale, scale);

    const vw = width / scale;
    const vh = height / scale;
    const stave = new Stave(6, vh / 2 - VEX_LINE_GAP * 6, Math.max(120, vw - 12));
    stave.addClef(clef);
    if (keySignature && keySignature !== 'C') stave.addKeySignature(keySignature);
    stave.setContext(ctx).draw();

    const svg = host.querySelector('svg');
    if (svg) {
      svg.setAttribute('class', 'staff-svg');
      svg.style.display = 'block';
    }

    // Read the real geometry back out and convert to CSS pixels, so the ink layer
    // and the recogniser measure against exactly the lines that were drawn.
    const layout: StaffLayout = {
      lineGap: stave.getSpacingBetweenLines() * scale,
      bottomLineY: stave.getYForLine(4) * scale,
      startX: stave.getX() * scale,
      endX: (stave.getX() + stave.getWidth()) * scale,
      writingX: stave.getNoteStartX() * scale,
    };

    // Ledger guides: dim marks at the positions a note would need a ledger line,
    // so writing above or below the staff still has something to aim at.
    const guides = guidesRef.current;
    if (guides) {
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      guides.width = Math.round(width * dpr);
      guides.height = Math.round(height * dpr);
      guides.style.width = `${width}px`;
      guides.style.height = `${height}px`;
      const g = guides.getContext('2d');
      if (g) {
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.clearRect(0, 0, width, height);
        if (showLedgerGuides) {
          g.strokeStyle = 'rgba(18,22,29,0.16)';
          g.lineWidth = 1;
          g.setLineDash([3, 7]);
          for (const position of [-6, -4, -2, 10, 12, 14]) {
            const y = layout.bottomLineY - (position * layout.lineGap) / 2;
            if (y < 6 || y > height - 6) continue;
            g.beginPath();
            g.moveTo(layout.writingX, y);
            g.lineTo(layout.endX - 8, y);
            g.stroke();
          }
        }
      }
    }

    onLayoutRef.current(layout);
  }, [clef, keySignature, width, height, showLedgerGuides, studio]);

  return (
    <>
      <div ref={hostRef} className="staff-host" aria-hidden="true" />
      <canvas ref={guidesRef} className="staff-guides" aria-hidden="true" />
    </>
  );
}
